// Returns to vendors in Inventory (BL-013 PR 2, on PR 1's server): draft parts going back to a vendor
// from the warehouse or a truck (vendorReturns.create), from scratch or from a PO's received lines,
// with why (the return types) and the vendor's RMA when they give one (vendorReturns.update); send it
// back, which takes the stock off at what it is carried at (vendorReturns.ship); cancel a draft; and
// take back one the vendor refused and shipped back (vendorReturns.receiveBack). Over vendorReturns.list
// and get. The vendor's credit and the other ways a return closes are the bookkeeper's, in Payables.
// The owner and managers keep the return types (returnTypes.save). A refusal says the server's words.

import { useState } from 'react';
import { client } from '../api.ts';
import { dayText } from '../books/model.ts';
import { Empty, Loading, Money, Panel, errorText, useQuery } from '../books/ui.tsx';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { ItemPicker } from './Orders.tsx';
import { qtyText, type StockLocation } from './model.ts';
import type { Vendor } from './orders-model.ts';
import {
  lineProblems,
  linesInput,
  lineText,
  RETURN_FILTERS,
  returnStatusText,
  type LineForm,
  type ReturnStatus,
  type ReturnType,
  type VendorReturn,
} from './returns-model.ts';
import './inventory.css';

/** What starts a new return: a PO's received lines, or nothing (loose stock). */
export interface ReturnStart {
  vendorId: string;
  locationId: string;
  lines: LineForm[];
}

function useWrite() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async <T,>(f: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setError(null);
    try {
      return await f();
    } catch (e) {
      setError(errorText(e));
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

export function Returns({
  today,
  toast,
  mayManage,
  maySetup,
  locations,
  start,
  onStarted,
}: {
  today: string;
  toast: (m: string) => void;
  mayManage: boolean;
  maySetup: boolean;
  locations: StockLocation[];
  /** A return to draft from a PO's sheet (Send some back). */
  start?: ReturnStart | null;
  onStarted?: () => void;
}) {
  const [status, setStatus] = useState<ReturnStatus | null>(null);
  const q = useQuery<{ returns: VendorReturn[] }>('vendorReturns.list', status ? { status } : {});
  const vendors = useQuery<{ vendors: Vendor[] }>('vendors.list', {});
  const types = useQuery<{ types: ReturnType[] }>('returnTypes.list', {});
  const [openId, setOpenId] = useState<string | null>(null);
  const [making, setMaking] = useState<ReturnStart | null>(start ?? null);
  const changed = async (said: string, id?: string) => {
    toast(said);
    await q.reload();
    if (id) setOpenId(id);
  };
  const stop = () => {
    setMaking(null);
    onStarted?.();
  };
  return (
    <>
      <Panel
        title="Returns"
        aside={
          <div className="row-actions">
            <div className="pills" role="group" aria-label="Which returns">
              {RETURN_FILTERS.map((f) => (
                <button
                  key={f.label}
                  type="button"
                  className="pill"
                  aria-pressed={status === f.status}
                  onClick={() => setStatus(f.status)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {mayManage && !making && (
              <button
                type="button"
                className="btn"
                onClick={() =>
                  setMaking({
                    vendorId: '',
                    locationId: locations.find((l) => l.kind === 'warehouse')?.id ?? '',
                    lines: [],
                  })
                }
              >
                Return to a vendor
              </button>
            )}
          </div>
        }
      >
        {!q.data ? (
          <Loading
            error={q.error}
            what="the returns"
            kind="table"
            cols={['Return', 'Vendor', 'From', 'Value', 'Status']}
            retry={() => void q.reload()}
          />
        ) : q.data.returns.length === 0 ? (
          <Empty>
            {status
              ? 'No returns here.'
              : `No returns yet.${mayManage ? ' When parts go back to a vendor, draft the return here, or from the purchase order they came on.' : ''}`}
          </Empty>
        ) : (
          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table" aria-label="Returns to vendors">
              <thead>
                <tr>
                  <th>Return</th>
                  <th>Vendor</th>
                  <th>From</th>
                  <th className="num">Value</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {q.data.returns.map((r) => (
                  <tr key={r.id} className={openId === r.id ? 'on' : undefined}>
                    <td>
                      <button
                        type="button"
                        className="row-btn"
                        aria-expanded={openId === r.id}
                        onClick={() => setOpenId(openId === r.id ? null : r.id)}
                      >
                        <b>{r.number}</b>
                        <span className="muted small">{r.returnTypeName}</span>
                        {r.rma && <span className="muted small">RMA {r.rma}</span>}
                      </button>
                    </td>
                    <td>{r.vendorName}</td>
                    <td>{r.locationName}</td>
                    <td className="num">
                      <Money cents={r.valueCents ?? r.expectedCents} />
                    </td>
                    <td>
                      <span
                        className={`chip say${r.status === 'closed' ? ' ok' : r.status === 'sent' ? ' warn' : ''}`}
                      >
                        {returnStatusText(r)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {making && (
        <Panel
          title="A return to a vendor"
          aside={
            <button type="button" className="btn quiet" onClick={stop}>
              Cancel
            </button>
          }
        >
          <NewReturn
            start={making}
            vendors={(vendors.data?.vendors ?? []).filter((v) => v.active)}
            locations={locations}
            types={types.data?.types ?? []}
            onMade={(r) => {
              stop();
              void changed(`${r.number} drafted for ${r.vendorName}. Send it back when it goes.`, r.id);
            }}
          />
        </Panel>
      )}

      {openId && (
        <OneReturn
          key={openId}
          id={openId}
          today={today}
          mayManage={mayManage}
          onChanged={(said) => void changed(said, openId)}
          onClose={() => setOpenId(null)}
        />
      )}

      {maySetup && <ReturnTypes toast={toast} onChanged={() => void types.reload()} />}
    </>
  );
}

// ── a new return ──

function NewReturn({
  start,
  vendors,
  locations,
  types,
  onMade,
}: {
  start: ReturnStart;
  vendors: Vendor[];
  locations: StockLocation[];
  types: ReturnType[];
  onMade: (r: VendorReturn) => void;
}) {
  const fromPo = start.lines.some((l) => l.poLineId);
  const [vendorId, setVendorId] = useState(start.vendorId);
  const [locationId, setLocationId] = useState(start.locationId);
  const [returnType, setReturnType] = useState(types[0]?.code ?? 'defective');
  const [rma, setRma] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<LineForm[]>(start.lines);
  const [tried, setTried] = useState(false);
  const w = useWrite();
  const save = useSaveKey(JSON.stringify({ vendorId, locationId, returnType, rma, note, lines }));
  const problems = [
    ...(!vendorId ? ['Pick the vendor.'] : []),
    ...(!locationId ? ['Pick where it goes back from.'] : []),
    ...lineProblems(lines),
  ];
  const set = (i: number, patch: Partial<LineForm>) =>
    setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const make = async () => {
    setTried(true);
    if (problems.length) return;
    const r = await w.run(() =>
      client.call<VendorReturn>(
        'vendorReturns.create',
        {
          vendorId,
          locationId,
          returnType,
          ...(rma.trim() ? { rma: rma.trim() } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
          lines: linesInput(lines),
        },
        { idempotencyKey: save.key },
      ),
    );
    if (r) onMade(r);
  };
  return (
    <form
      className="fgrid"
      aria-label="A return to a vendor"
      onSubmit={(e) => {
        e.preventDefault();
        if (!w.busy) void make();
      }}
    >
      <label>
        <span className="flabel">Vendor</span>
        <select
          className="field"
          value={vendorId}
          disabled={fromPo}
          onChange={(e) => setVendorId(e.target.value)}
        >
          <option value="">Pick a vendor…</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="flabel">Goes back from</span>
        <select className="field" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="flabel">Why</span>
        <select className="field" value={returnType} onChange={(e) => setReturnType(e.target.value)}>
          {types.map((t) => (
            <option key={t.code} value={t.code}>
              {t.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span className="flabel">Their RMA (optional)</span>
        <input
          className="field"
          maxLength={100}
          value={rma}
          placeholder="The vendor’s return number"
          onChange={(e) => setRma(e.target.value)}
        />
      </label>
      {lines.length > 0 && (
        <ul className="po-lines full" aria-label="What goes back">
          {lines.map((l, i) => (
            <li key={`${l.itemId}:${l.poLineId ?? ''}`} className="po-line">
              <b className="po-line-name">
                {l.name}
                {l.most !== undefined && (
                  <span className="muted small block">
                    {qtyText(l.most)} came on {l.poNumber}
                  </span>
                )}
              </b>
              <label>
                <span className="flabel">How many (each)</span>
                <input
                  className="field"
                  inputMode="decimal"
                  value={l.quantity}
                  placeholder="0"
                  onChange={(e) => set(i, { quantity: e.target.value })}
                />
              </label>
              <label>
                <span className="flabel">Credit expected each</span>
                <input
                  className="field"
                  inputMode="decimal"
                  placeholder={l.poLineId ? 'The PO’s price' : 'What it’s carried at'}
                  value={l.credit}
                  onChange={(e) => set(i, { credit: e.target.value })}
                />
              </label>
              <button
                type="button"
                className="btn quiet"
                aria-label={`Remove ${l.name}`}
                onClick={() => setLines(lines.filter((_, k) => k !== i))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {!fromPo && (
        <ItemPicker
          taken={lines.map((l) => l.itemId)}
          onPick={(item) =>
            setLines([...lines, { itemId: item.id, name: item.name, quantity: '1', credit: '' }])
          }
        />
      )}
      <label className="full">
        <span className="flabel">Note (optional)</span>
        <input className="field" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {tried && problems.length > 0 && (
        <div className="notice warn full" role="alert">
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="row-actions full">
        <SaveButton busy={w.busy} busyLabel="Drafting…" error={w.error} onClick={() => void make()}>
          Draft the return
        </SaveButton>
      </div>
      <p className="muted small full">Nothing comes off stock until you send it back.</p>
    </form>
  );
}

// ── one return ──

function OneReturn({
  id,
  today,
  mayManage,
  onChanged,
  onClose,
}: {
  id: string;
  today: string;
  mayManage: boolean;
  onChanged: (said: string) => void;
  onClose: () => void;
}) {
  const q = useQuery<VendorReturn>('vendorReturns.get', { id });
  const [sheet, setSheet] = useState<'ship' | 'cancel' | 'back' | null>(null);
  const [rma, setRma] = useState<string | null>(null);
  const saveRma = useWrite();
  const r = q.data;
  const done = async (said: string) => {
    setSheet(null);
    await q.reload();
    onChanged(said);
  };
  return (
    <Panel
      title={r ? `${r.number} · ${r.vendorName}` : 'Return'}
      aside={
        <button type="button" className="btn quiet" onClick={onClose}>
          Close
        </button>
      }
    >
      {!r ? (
        <Loading error={q.error} what="the return" retry={() => void q.reload()} />
      ) : (
        <div className="po-detail">
          <div className="row">
            <span className={`chip${r.status === 'closed' ? ' ok' : ''}`}>{returnStatusText(r)}</span>
            <span className="muted small">
              {r.returnTypeName} · from {r.locationName}
              {r.sentOn ? ` · sent back ${dayText(r.sentOn)}` : ''}
            </span>
          </div>
          {r.status === 'cancelled' && r.cancelReason && (
            <p className="notice">Cancelled: {r.cancelReason}</p>
          )}
          {r.status === 'closed' && r.closeNote && <p className="small muted">{r.closeNote}</p>}
          {r.note && <p className="small muted">Note: {r.note}</p>}

          {rma === null ? (
            <p className="row">
              <span>{r.rma ? `Their RMA: ${r.rma}` : 'No RMA from the vendor yet.'}</span>
              {mayManage && (r.status === 'draft' || r.status === 'sent') && (
                <button type="button" className="btn quiet sm" onClick={() => setRma(r.rma ?? '')}>
                  {r.rma ? 'Change the RMA' : 'Add their RMA'}
                </button>
              )}
            </p>
          ) : (
            <div className="fgrid inline-form" role="group" aria-label="Their RMA">
              <label>
                <span className="flabel">Their RMA</span>
                <input
                  className="field"
                  maxLength={100}
                  value={rma}
                  onChange={(e) => setRma(e.target.value)}
                />
              </label>
              <div className="row-actions">
                <SaveButton
                  busy={saveRma.busy}
                  busyLabel="Saving…"
                  error={saveRma.error}
                  onClick={() =>
                    void saveRma
                      .run(() =>
                        client.call('vendorReturns.update', { returnId: r.id, rma: rma.trim() || null }),
                      )
                      .then((x) => {
                        if (x === null) return;
                        setRma(null);
                        void done(
                          rma.trim() ? `${r.number}’s RMA is ${rma.trim()}.` : `${r.number} has no RMA.`,
                        );
                      })
                  }
                >
                  Save the RMA
                </SaveButton>
                <button type="button" className="btn quiet" onClick={() => setRma(null)}>
                  Keep it
                </button>
              </div>
            </div>
          )}

          <div className="table-wrap" ref={scrollFocus}>
            <table className="grid-table" aria-label={`${r.number} lines`}>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Each</th>
                  <th className="num">Credit expected</th>
                  <th className="num">Carried at</th>
                  <th className="num">Credited</th>
                </tr>
              </thead>
              <tbody>
                {r.lines.map((l) => (
                  <tr key={l.id}>
                    <td>
                      {l.description}
                      {l.poNumber && <span className="muted small block">from {l.poNumber}</span>}
                      {l.tracked === false && <span className="muted small block">not kept in stock</span>}
                    </td>
                    <td className="num">{qtyText(l.quantity)}</td>
                    <td className="num">
                      <Money cents={l.expectedCents} />
                    </td>
                    <td className="num">{l.valueCents === null ? '—' : <Money cents={l.valueCents} />}</td>
                    <td className="num">{qtyText(l.creditedQty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {r.status === 'sent' && (
            <p className="small">
              <Money cents={r.openCents} /> is waiting for {r.vendorName}’s credit. The bookkeeper enters it
              in Payables.
            </p>
          )}
          {r.credits.length > 0 && (
            <ul className="rows" aria-label="Their credit notes">
              {r.credits.map((c) => (
                <li key={c.id} className="small">
                  {c.number} · {c.vendorRef} · <Money cents={c.totalCents} /> ·{' '}
                  {c.status === 'draft' ? 'waiting for approval' : 'approved'}
                </li>
              ))}
            </ul>
          )}

          {mayManage && (
            <div className="row-actions">
              {r.status === 'draft' && (
                <button type="button" className="btn primary" onClick={() => setSheet('ship')}>
                  Send it back
                </button>
              )}
              {r.status === 'draft' && (
                <button type="button" className="btn" onClick={() => setSheet('cancel')}>
                  Cancel the return
                </button>
              )}
              {r.status === 'sent' && r.credits.length === 0 && (
                <button type="button" className="btn" onClick={() => setSheet('back')}>
                  They sent it back
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {r && sheet === 'ship' && (
        <ShipSheet r={r} today={today} onClose={() => setSheet(null)} onDone={(said) => void done(said)} />
      )}
      {r && sheet === 'cancel' && (
        <CancelSheet r={r} onClose={() => setSheet(null)} onDone={(said) => void done(said)} />
      )}
      {r && sheet === 'back' && (
        <BackSheet r={r} today={today} onClose={() => setSheet(null)} onDone={(said) => void done(said)} />
      )}
    </Panel>
  );
}

function ShipSheet({
  r,
  today,
  onClose,
  onDone,
}: {
  r: VendorReturn;
  today: string;
  onClose: () => void;
  onDone: (said: string) => void;
}) {
  const [sentOn, setSentOn] = useState(today);
  const w = useWrite();
  return (
    <MoneySheet
      title={`Send ${r.number} back to ${r.vendorName}?`}
      outcome={`${r.lines.map(lineText).join(', ')} come off ${r.locationName} at what they’re carried at, and wait for ${r.vendorName}’s credit.`}
      confirm="Send it back"
      ready={!!sentOn}
      busy={w.busy}
      error={w.error}
      onConfirm={() =>
        void w
          .run(() => client.call<VendorReturn>('vendorReturns.ship', { returnId: r.id, sentOn }))
          .then((x) => {
            if (x) onDone(`${x.number} is on its way back: ${x.vendorName} owes a credit for it.`);
          })
      }
      onClose={onClose}
    >
      <label>
        Went back on
        <input
          className="field"
          type="date"
          value={sentOn}
          max={today}
          onChange={(e) => setSentOn(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}

function CancelSheet({
  r,
  onClose,
  onDone,
}: {
  r: VendorReturn;
  onClose: () => void;
  onDone: (said: string) => void;
}) {
  const [reason, setReason] = useState('');
  const w = useWrite();
  return (
    <MoneySheet
      title={`Cancel ${r.number}?`}
      outcome="Nothing went back, so nothing on stock changes."
      confirm="Cancel the return"
      ready={!!reason.trim()}
      busy={w.busy}
      error={w.error}
      onConfirm={() =>
        void w
          .run(() => client.call('vendorReturns.cancel', { returnId: r.id, reason: reason.trim() }))
          .then((x) => {
            if (x) onDone(`${r.number} is cancelled.`);
          })
      }
      onClose={onClose}
    >
      <label>
        Why (required)
        <input
          className="field"
          maxLength={300}
          value={reason}
          placeholder="They’ll swap it on the counter; it works after all"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}

function BackSheet({
  r,
  today,
  onClose,
  onDone,
}: {
  r: VendorReturn;
  today: string;
  onClose: () => void;
  onDone: (said: string) => void;
}) {
  const [receivedOn, setReceivedOn] = useState(today);
  const [note, setNote] = useState('');
  const w = useWrite();
  return (
    <MoneySheet
      title={`${r.vendorName} sent ${r.number} back?`}
      outcome={`${r.lines.map(lineText).join(', ')} go on ${r.locationName} again at the value they left at, and ${r.number} closes with no credit.`}
      confirm="Put them back on stock"
      ready={!!receivedOn}
      busy={w.busy}
      error={w.error}
      onConfirm={() =>
        void w
          .run(() =>
            client.call('vendorReturns.receiveBack', {
              returnId: r.id,
              receivedOn,
              ...(note.trim() ? { note: note.trim() } : {}),
            }),
          )
          .then((x) => {
            if (x) onDone(`${r.number}’s parts are back on ${r.locationName}.`);
          })
      }
      onClose={onClose}
    >
      <label>
        Came back on
        <input
          className="field"
          type="date"
          value={receivedOn}
          max={today}
          onChange={(e) => setReceivedOn(e.target.value)}
        />
      </label>
      <label>
        Why they refused it (optional)
        <input className="field" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
    </MoneySheet>
  );
}

// ── the return types (owner and managers) ──

function ReturnTypes({ toast, onChanged }: { toast: (m: string) => void; onChanged: () => void }) {
  const q = useQuery<{ types: ReturnType[] }>('returnTypes.list', { includeInactive: true });
  const [name, setName] = useState('');
  const w = useWrite();
  const save = async (t: { code: string; name: string; active: boolean }, said: string) => {
    const x = await w.run(() => client.call('returnTypes.save', t));
    if (x === null) return;
    toast(said);
    setName('');
    await q.reload();
    onChanged();
  };
  const code = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'r_$1')
    .slice(0, 30);
  return (
    <Panel title="Why parts go back">
      {!q.data ? (
        <Loading error={q.error} what="the return types" retry={() => void q.reload()} />
      ) : (
        <>
          <ul className="rows" aria-label="Return types">
            {q.data.types.map((t) => (
              <li key={t.code} className="inv-row">
                <div className="inv-main">
                  <strong>{t.name}</strong>
                  {!t.active && <span className="muted small">retired</span>}
                </div>
                <div className="row-actions">
                  <button
                    type="button"
                    className="btn quiet sm"
                    disabled={w.busy}
                    aria-label={`${t.active ? 'Retire' : 'Use again'} ${t.name}`}
                    onClick={() =>
                      void save(
                        { ...t, active: !t.active },
                        t.active ? `${t.name} is retired.` : `${t.name} is in use again.`,
                      )
                    }
                  >
                    {t.active ? 'Retire' : 'Use again'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <form
            className="fgrid inline-form"
            aria-label="Add a return type"
            onSubmit={(e) => {
              e.preventDefault();
              if (code && !w.busy)
                void save({ code, name: name.trim(), active: true }, `${name.trim()} added.`);
            }}
          >
            <label>
              <span className="flabel">A new reason</span>
              <input
                className="field"
                maxLength={60}
                value={name}
                placeholder="Warranty"
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <div className="row-actions">
              <button type="submit" className="btn" disabled={!code || w.busy}>
                Add it
              </button>
            </div>
            {w.error && (
              <p className="error full" role="alert">
                {w.error}
              </p>
            )}
          </form>
        </>
      )}
    </Panel>
  );
}
